import React, { Fragment } from 'react';
import { MaybeNil } from '../../utils/interfaces';
import * as ExclusiveButtonGroupUI from './ExclusiveButtonGroupUI';
import { isEqual } from 'lodash';
import KubitTooltip from '../KubitTooltip';
import { IStyled } from '../../styles/types';

export interface IExclusiveButtonOption<T> {
  label: JSX.Element | string;
  value: T;
  hint?: string;
}

interface IProps<T> extends IStyled {
  selectedValue: MaybeNil<T>;
  options: IExclusiveButtonOption<T>[];
  onChange: (
    selectedValue: T,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => unknown;
  label?: string;
}

export type IExclusiveButtonOnChange<T> = IProps<T>['onChange'];

export const ExclusiveButtonGroup = <T,>({
  options,
  onChange,
  selectedValue,
  label,
  className,
}: IProps<T>) => {
  const renderOption = (option: IExclusiveButtonOption<T>, index: number) => {
    const isActive = isEqual(option.value, selectedValue);
    return (
      <ExclusiveButtonGroupUI.Option
        type="button"
        disabled={isActive}
        onClick={(event) => onChange(options[index].value, event)}
        $active={isActive}
      >
        {option.label}
      </ExclusiveButtonGroupUI.Option>
    );
  };
  return (
    <>
      {label && (
        <ExclusiveButtonGroupUI.Label>{label}</ExclusiveButtonGroupUI.Label>
      )}
      <ExclusiveButtonGroupUI.OptionsWrapper className={className}>
        {options.map((option, index) => {
          const key = `exclusive-button-group-option-${index}-${option.value}`;

          if (option?.hint) {
            return (
              <KubitTooltip
                key={key}
                content={option.hint}
                placement="top-start"
                noOpenDelay={true}
              >
                {renderOption(option, index)}
              </KubitTooltip>
            );
          }

          return <Fragment key={key}>{renderOption(option, index)}</Fragment>;
        })}
      </ExclusiveButtonGroupUI.OptionsWrapper>
    </>
  );
};
